import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Spinner, Modal, ModalHeader, ModalBody, Button, Input, Label, FormFeedback } from 'reactstrap';
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//Import Breadcrumb
import BreadCrumb from "../../../Components/Common/BreadCrumb";
//redux
import { Link } from "react-router-dom";
import { 
  getEcommerceCategories, 
  addNewEcommerceCategory, 
  updateEcommerceCategory, 
  deleteEcommerceCategory,
  getProducts 
} from "../../../slices/ecommerce/thunk";
import { getUserRole } from "../../../helpers/role_helper";
import { getLoggedinUser } from "../../../api";
import DeleteModal from "../../../Components/Common/DeleteModal";

const getCategoryImageUrl = (img) => {
  if (!img) return null;
  if (img.startsWith("http")) return img;
  const base = (process.env.REACT_APP_API_URL || "http://localhost:5001/api").replace("/api", "");
  return `${base}${img.startsWith("/") ? "" : "/"}${img}`;
};

const EcommerceCategory = (props) => {
  document.title = "Product Category - Dashboard";
  const dispatch = useDispatch();

  // Get user info for filtering
  const userProfile = getLoggedinUser();
  const userRole = getUserRole(userProfile);
  const userId = userProfile?.id || userProfile?.uid || userProfile?._id;

  // Modal states
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [categoryData, setCategoryData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // New Category states
  const [categoryImage, setCategoryImage] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Redux selectors
  const ecommerceData = createSelector(
    (state) => state.Ecommerce,
    (ecommerce) => ({
      categories: ecommerce.categories || [],
      products: ecommerce.products || [],
      loading: ecommerce.loading || false,
      error: ecommerce.error
    })
  );
  const { categories, products, loading, error } = useSelector(ecommerceData);

  // Fetch categories on component mount (common categories only - no seller filter)
  useEffect(() => {
    dispatch(getEcommerceCategories({}));
    dispatch(getProducts()); // Fetch products to check for deletion
  }, [dispatch]);

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (validTypes.includes(file.type)) {
        setCategoryImage(file);
      } else {
        alert("Only JPG, JPEG, PNG, and WEBP image files are allowed!");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (validTypes.includes(file.type)) {
        setCategoryImage(file);
      } else {
        alert("Only JPG, JPEG, PNG, and WEBP image files are allowed!");
      }
    }
  };

  // Validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: categoryData?.name || "",
      description: categoryData?.description || "",
      status: categoryData?.originalStatus || "active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Category name is required"),
      description: Yup.string().nullable(),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("description", values.description || "");
        formData.append("status", values.status || "active");

        if (userRole === "seller" && userId) {
          formData.append("seller_id", userId);
        }

        if (categoryImage) {
          formData.append("categoryImage", categoryImage);
        } else if (imageRemoved) {
          formData.append("image", "null");
        }

        if (isEdit) {
          const updateCategory = {
            id: categoryData.id,
            formData: formData,
          };
          await dispatch(updateEcommerceCategory(updateCategory)).unwrap();
        } else {
          const newCategory = {
            formData: formData,
          };
          await dispatch(addNewEcommerceCategory(newCategory)).unwrap();
        }
        // Close modal and reset form only after successful submission
        setCategoryImage(null);
        setImageRemoved(false);
        toggle();
        validation.resetForm();
      } catch (error) {
        // Error toast is already handled in the thunk
        // Don't close modal on error so user can fix and retry
        console.error("Category operation failed:", error);
      }
    },
  });

  // Toggle modal
  const toggle = () => {
    if (modal) {
      setModal(false);
      setCategoryData(null);
      setIsEdit(false);
      setCategoryImage(null);
      setImageRemoved(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  // Handle edit
  const handleEditClick = (category) => {
    setCategoryData(category);
    setIsEdit(true);
    setModal(true);
    validation.setFieldValue("name", category.name || category.category);
    validation.setFieldValue("description", category.description || "");
    validation.setFieldValue("status", category.originalStatus || "active");
  };

  // Handle delete click
  const handleDeleteClick = (category) => {
    setDeleteId(category.id);
    setDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteCategory = () => {
    // Check if any products exist under this category
    const productsInCategory = products.filter(
      (product) => product.category_id === deleteId || product.categoryId === deleteId
    );

    if (productsInCategory.length > 0) {
      alert(`Cannot delete category. There are ${productsInCategory.length} product(s) associated with this category. Please remove or reassign those products first.`);
      setDeleteModal(false);
      return;
    }

    dispatch(deleteEcommerceCategory(deleteId));
    setDeleteModal(false);
    setDeleteId(null);
  };

  // Map database categories to display format
  const categoryList = categories.map((cat) => ({
    id: cat.id || cat._id,
    name: cat.name,
    category: cat.name,
    status: cat.status === 'active' ? 'Approved' : cat.status === 'inactive' ? 'Rejected' : 'Pending',
    originalStatus: cat.status || 'active',
    slug: cat.slug,
    description: cat.description,
    image: cat.image,
    seller_id: cat.seller_id
  }));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Product Category" pageTitle="Seller" />
        <Row>
          <Card>
            <CardBody>
              <div className="listjs-table" id="customerList">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Product Category</h5>
                  </div>
                  <div className="col-sm-auto">
                    {userRole !== "seller" && (
                    <div className="d-flex gap-1 flex-wrap">
                      <button 
                        type="button" 
                        className="btn btn-info"
                        onClick={toggle}
                      >
                        <i className="ri-add-circle-line align-bottom me-1"></i>{" "}
                        Add Category
                      </button>
                    </div>
                    )}
                  </div>
                </Row>

                <div className="table-responsive table-card mt-3 mb-1">
                  <table className="table align-middle table-nowrap" id="customerTable">
                    <thead className="table-light">
                      <tr>
                        <th scope="col" style={{ width: "50px" }}>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="checkAll" value="option" />
                          </div>
                        </th>
                        <th className="sort" data-sort="id">Id</th>
                        <th>Image</th>
                        <th className="sort" data-sort="name">Name</th>
                        <th className="sort" data-sort="status">Status</th>
                        {userRole !== "seller" && <th className="sort" data-sort="action">Operations</th>}
                      </tr>
                    </thead>
                    <tbody className="list form-check-all">
                      {loading ? (
                        <tr>
                          <td colSpan={userRole === "seller" ? 5 : 6} className="text-center">
                            <Spinner className="me-2" /> Loading categories...
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan={userRole === "seller" ? 5 : 6} className="text-center text-danger">
                            <div>
                              <strong>Error loading categories:</strong>
                              <br />
                              {typeof error === 'string' ? error : (error?.message || error?.error || JSON.stringify(error) || 'Unknown error')}
                              <br />
                              <small className="text-muted">
                                Make sure the backend server is running on http://localhost:5001
                              </small>
                            </div>
                          </td>
                        </tr>
                      ) : categoryList.length === 0 ? (
                        <tr>
                          <td colSpan={userRole === "seller" ? 5 : 6} className="text-center">
                            {userRole === "seller" ? "No categories found." : "No categories found. Add your first category!"}
                          </td>
                        </tr>
                      ) : (
                        categoryList.map((item, index) => (
                        <tr key={item.id}>

                          {/* Checkbox */}
                          <th scope="row">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="checkAll"
                                value={item.id}
                              />
                            </div>
                          </th>

                          {/* ID */}
                          <td className="id">{item.id}</td>

                          {/* Image */}
                          <td>
                            {item.image ? (
                              <img 
                                src={getCategoryImageUrl(item.image)} 
                                alt={item.name} 
                                className="avatar-xs rounded bg-light"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div 
                                className="avatar-xs rounded bg-light d-flex align-items-center justify-content-center text-muted"
                                style={{ width: '40px', height: '40px', fontSize: '20px' }}
                              >
                                <i className="ri-image-line"></i>
                              </div>
                            )}
                          </td>

                          {/* Name */}
                          <td className="name">{item.category}</td>

                          <td className="status">
                            <span
                              className={`badge ${item.status === "Approved"
                                ? "bg-success"
                                : item.status === "Rejected"
                                  ? "bg-danger"
                                  : "bg-warning"
                                }`}
                            >
                              {item.status}
                            </span>
                          </td>

                          {/* Operations - admin only */}
                          {userRole !== "seller" && (
                          <td>
                            <div className="d-flex gap-2">
                              <div className="edit">
                                <button
                                  className="btn btn-sm btn-success edit-item-btn"
                                  onClick={() => handleEditClick(item)}
                                >
                                  <i className="ri-edit-line"></i>
                                </button>
                              </div>

                              <div className="remove">
                                <button
                                  className="btn btn-sm btn-danger remove-item-btn"
                                  onClick={() => handleDeleteClick(item)}
                                >
                                  <i className="ri-delete-bin-2-line"></i>
                                </button>
                              </div>
                            </div>
                          </td>
                          )}

                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  <div className="noresult" style={{ display: "none" }}>
                    <div className="text-center">
                      <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop"
                        colors="primary:#121331,secondary:#08a88a" style={{ width: "75px", height: "75px" }}>
                      </lord-icon>
                      <h5 className="mt-2">Sorry! No Result Found</h5>
                      <p className="text-muted mb-0">We've searched more than 150+ Orders We did not find any
                        orders for you search.</p>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <div className="pagination-wrap hstack gap-2">
                    <Link className="page-item pagination-prev disabled" to="#">
                      Previous
                    </Link>
                    <ul className="pagination listjs-pagination mb-0"></ul>
                    <Link className="page-item pagination-next" to="#">
                      Next
                    </Link>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Row>
      </Container>

      {/* Add/Edit Category Modal */}
      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>
          {isEdit ? "Edit Category" : "Add Category"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}>
            {/* Category Image Drag & Drop */}
            <div className="mb-3">
              <Label className="form-label">Category Image</Label>
              {(categoryImage || (categoryData?.image && !imageRemoved)) ? (
                <div className="position-relative border rounded-3 p-2 text-center bg-light mb-2">
                  <img 
                    src={categoryImage ? URL.createObjectURL(categoryImage) : getCategoryImageUrl(categoryData.image)} 
                    alt="Category Preview" 
                    className="img-fluid rounded-3" 
                    style={{ maxHeight: '180px', objectFit: 'contain', width: '100%' }}
                  />
                  <div className="position-absolute top-0 end-0 m-2 d-flex gap-1">
                    <Button 
                      type="button" 
                      color="danger" 
                      size="sm" 
                      className="btn-icon" 
                      title="Remove Image"
                      onClick={() => {
                        setCategoryImage(null);
                        if (isEdit) {
                          setImageRemoved(true);
                        }
                      }}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </Button>
                    <Button 
                      type="button" 
                      color="primary" 
                      size="sm" 
                      className="btn-icon" 
                      title="Change Image"
                      onClick={() => document.getElementById('categoryImageInput').click()}
                    >
                      <i className="ri-pencil-line"></i>
                    </Button>
                  </div>
                  <input 
                    type="file" 
                    id="categoryImageInput" 
                    className="d-none" 
                    accept=".jpg,.jpeg,.png,.webp" 
                    onChange={handleFileChange}
                  />
                </div>
              ) : (
                <div 
                  className={`border rounded-3 p-4 text-center cursor-pointer mb-2`}
                  style={{
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                    borderColor: isDragOver ? '#4b38b3' : '#ced4da',
                    backgroundColor: isDragOver ? 'rgba(75, 56, 179, 0.05)' : '#f8f9fa',
                    transition: 'all 0.2s ease',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('categoryImageInput').click()}
                >
                  <input 
                    type="file" 
                    id="categoryImageInput" 
                    className="d-none" 
                    accept=".jpg,.jpeg,.png,.webp" 
                    onChange={handleFileChange}
                  />
                  <i className="ri-upload-cloud-2-line text-muted display-5"></i>
                  <h5 className="mt-2 mb-1">Drag & drop your image here</h5>
                  <p className="text-muted font-size-13 mb-0">or click to browse from files</p>
                  <p className="text-muted font-size-11 mb-0">(JPG, JPEG, PNG, WEBP, max 5MB)</p>
                </div>
              )}
            </div>

            {/* Category Name */}
            <div className="mb-3">
              <Label htmlFor="name" className="form-label">
                Category Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                className="form-control"
                id="name"
                name="name"
                placeholder="Enter category name"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.name || ""}
                invalid={
                  validation.touched.name && validation.errors.name ? true : false
                }
              />
              {validation.touched.name && validation.errors.name ? (
                <FormFeedback type="invalid">
                  {validation.errors.name}
                </FormFeedback>
              ) : null}
            </div>

            {/* Description */}
            <div className="mb-3">
              <Label htmlFor="description" className="form-label">
                Description
              </Label>
              <Input
                type="textarea"
                className="form-control"
                id="description"
                name="description"
                placeholder="Enter category description"
                rows="3"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.description || ""}
              />
            </div>

            {/* Status */}
            <div className="mb-3">
              <Label htmlFor="status" className="form-label">
                Status
              </Label>
              <select
                className="form-select"
                id="status"
                name="status"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.status || "active"}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={toggle}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                {isEdit ? "Update" : "Add"} Category
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteCategory}
        onCloseClick={() => {
          setDeleteModal(false);
          setDeleteId(null);
        }}
      />

      {/* Toast Container */}
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default EcommerceCategory;
